import React from "react";
import { useNavigate } from "react-router-dom";
import { money } from "../../utils/format";
import { can } from "./permissions";
import DataTable from "../../components/DataTable";
import Loader from "../../components/Loader";
import WhatsAppIcon from "../../components/WhatsAppIcon";

// The websites table used on the Overview, Websites and Payments tabs. onDelete (Websites tab) adds Delete for a Super
// Admin, and Request deletion (or Cancel request, onCancelDeletion) for a store owner or partner. onMessage (Websites
// tab, Super Admin) shows the customer's email and phone with WhatsApp buttons: a welcome message (crossed off with
// onWelcomeSent once sent) and a custom message.
// A free trial is deleted 7 days after it ends unless it moves to a paid plan (backend services/stores/trialCleanup.js)
// (in UTC, like the expiry date: the server keeps both as the end of that day in UTC)
export const deleteDate = (site) => new Date(site.deletesAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

export default function SiteTable({ sites, user, loading, onToggle, onPay, onLinkDomain, onDelete, onCancelDeletion, onMessage, onWelcomeSent, onProductSetup }) {
  const navigate = useNavigate();
  const isSuper = user.role === "superadmin";
  const isSupport = user.role === "support"; // edit websites only; plans show Free trial or Paid, no amounts
  const rate = (s) => (s.plan === "Free trial" ? "Free trial" : `${money(s.price)}/mo`);

  return (
    <DataTable>
        <thead>
          <tr><th>WEBSITE</th><th>PLAN</th><th>START DATE</th><th>EXPIRY / DUE IN</th><th>STATUS</th><th>ACTIONS</th></tr>
        </thead>
        <tbody>
          {sites.map((s) => {
            const left = s.daysRemaining;
            return (
              <tr key={s.id}>
                <td className="site-td">
                  <div className="site-cell-wrap">
                    <div className="site-name-cell">
                      <span className={`site-mark mark-${s.id}`}>{(s.name || "S").slice(0, 1)}</span>
                      <div className="site-info-stack">
                        <div className="site-name-row">
                          <b>{s.name}</b>
                          {/* Orders still to send out, in red; opens them */}
                          {s.deletionRequest && <span className="site-deletion-pill" title={s.deletionRequest.reason || ""}>Deletion requested</span>}
                          {/* Free product setup: asked by the owner; Ownezzy's team marks it done */}
                          {s.productSetup && !s.productSetup.doneAt && <span className="site-setup-pill" title="The owner asked our team to add their first 20 products">🎁 Product setup requested</span>}
                          {s.productSetup && !s.productSetup.doneAt && onProductSetup && <button type="button" className="site-setup-done" onClick={() => onProductSetup(s, true)}>✓ Mark done</button>}
                          {s.productSetup && s.productSetup.doneAt && onProductSetup && <span className="site-setup-pill is-done" title={`Done${s.productSetup.doneBy ? ` by ${s.productSetup.doneBy}` : ""}`}>🎁 Setup done</span>}
                          {s.pendingOrders > 0 && can(user, "orders") && (
                            <button className="site-pending" onClick={() => navigate(`/admin/sites/${s.id}/orders`)} title="Open these orders">
                              {s.pendingOrders} pending order{s.pendingOrders === 1 ? "" : "s"}
                            </button>
                          )}
                        </div>
                        <small>{isSuper || isSupport ? `${s.owner}${s.franchiseName ? ` · ${s.franchiseName}` : ""}` : s.domain}</small>
                        {isSuper && onMessage && (s.email || s.phone) && (
                          <div className="site-contact-row">
                            <small className="site-contact">{[s.email, s.phone].filter(Boolean).join(" · ")}</small>
                            <span className="site-wa-actions">
                              {s.welcomeSentAt ? (
                                <button type="button" className="site-wa-done" onClick={() => onWelcomeSent(s, false)} title="Welcome message sent. Tap to undo.">✓ Welcome sent</button>
                              ) : (
                                <span className="site-wa-chip">
                                  <button type="button" className="site-wa-btn" onClick={() => onMessage(s, "welcome")} title="WhatsApp: welcome message"><WhatsAppIcon size={14} /> Welcome</button>
                                  <button type="button" className="site-wa-x" onClick={() => onWelcomeSent(s, true)} title="Already sent: remove this option" aria-label="Welcome message already sent">×</button>
                                </span>
                              )}
                              <button type="button" className="site-wa-btn" onClick={() => onMessage(s, "custom")} title="WhatsApp: custom message"><WhatsAppIcon size={14} /> Custom</button>
                            </span>
                          </div>
                        )}
                        {s.url && <a className="site-link" href={s.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{s.domain || s.url.replace(/^https?:\/\//, "")} ↗</a>}
                      </div>
                    </div>
                    {isSuper && s.needsDomainLinking && (
                      <div className="site-domain-linking-alert" role="status">
                        <div className="site-domain-linking-message">
                          <span className="domain-alert-icon">⚠️</span>
                          <span>This subdomain <b>({s.subdomain || s.domain})</b> needs linking to main domain <b>({s.requestedDomain})</b></span>
                        </div>
                        {onLinkDomain && (
                          <button
                            type="button"
                            className="link-domain-inline-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onLinkDomain(s);
                            }}
                            title={`Link ${s.subdomain || s.domain} to ${s.requestedDomain}`}
                          >
                            Link domain →
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <span className={`plan-badge ${s.plan === "Paid" ? "plan-paid" : s.plan === "Free trial" ? "plan-trial" : "plan-expired"}`}>{s.plan}</span>
                  {isSupport ? null : s.domainPrice > 0 ? (
                    <div className="site-price-breakdown">
                      <small className="cell-sub">
                        Plan: {s.planKey === "trial" || s.plan === "Free trial" ? "Free trial" : money(s.planPrice || s.price)}
                      </small>
                      <small className="cell-sub domain-charge">
                        Domain: {money(s.domainPrice)}
                      </small>
                      {s.totalPaid > 0 && (
                        <small className="cell-sub total-paid">
                          Total paid: <b>{money(s.totalPaid)}</b>
                        </small>
                      )}
                    </div>
                  ) : (
                    <>
                      <small className="cell-sub">{rate(s)}</small>
                      {s.totalPaid > 0 && s.plan !== "Free trial" && (
                        <small className="cell-sub total-paid">Paid: {money(s.totalPaid)}</small>
                      )}
                    </>
                  )}
                </td>
                <td>{s.start}</td>
                <td>
                  <b>{s.expiry}</b>
                  <small className={`cell-sub ${left <= 3 ? "due-text" : ""}`}>
                    {left < 0 ? `${Math.abs(left)} days overdue` : left === 0 ? "Expires today" : `${left} days left`}
                  </small>
                  {s.deletesAt && left <= 3 && <small className="cell-sub due-text">Free trial: deleted on {deleteDate(s)} unless upgraded</small>}
                </td>
                <td>
                  <span className={`status-pill ${s.active ? "status-on" : "status-off"}`}><i />{s.active ? "Active" : "Inactive"}</span>
                </td>
                <td>
                  <div className="row-actions">
                    {can(user, "content") && <button className="edit-action" onClick={() => navigate(`/admin/sites/${s.id}/edit`)}>Edit site</button>}
                    {can(user, "orders") && <button className="toggle-action orders-action" onClick={() => navigate(`/admin/sites/${s.id}/orders`)}>Manage orders</button>}
                    {isSuper && s.needsDomainLinking && onLinkDomain && (
                      <button className="link-action" onClick={() => onLinkDomain(s)} title={`Link ${s.subdomain || s.domain} to ${s.requestedDomain}`}>Link domain</button>
                    )}
                    {isSuper && <button className={`toggle-action ${s.enabled ? "disable-action" : "enable-action"}`} onClick={() => onToggle(s)}>{s.enabled ? "Disable" : "Enable"}</button>}
                    {(!s.active || left <= 5) && !["staff", "support"].includes(user.role) && <button className="pay-action" onClick={() => onPay(s)}>{isSuper ? "Record payment" : "Pay now"}</button>}
                    {isSuper && onDelete && <button className="delete-action" onClick={() => onDelete(s)}>{s.deletionRequest ? "Review deletion" : "Delete"}</button>}
                    {!isSuper && onDelete && !s.deletionRequest && <button className="delete-action" onClick={() => onDelete(s)}>Request deletion</button>}
                    {!isSuper && onCancelDeletion && s.deletionRequest && <button className="toggle-action" onClick={() => onCancelDeletion(s)}>Cancel deletion request</button>}
                  </div>
                </td>
              </tr>
            );
          })}
          {!sites.length && (
            <tr><td colSpan="6" className="empty-row">{loading ? <Loader label="Loading websites" /> : "No websites match your search."}</td></tr>
          )}
        </tbody>
    </DataTable>
  );
}
